/**
 * Reason-code taxonomy for the 8 non-standard order actions
 * (cancel/refund/reship/refire/cancel_and_refund/cancel_subscription/
 * rescript/intake_override) —
 * mirrors system-backend's `ORDER_ACTION_REASON_CODES` (order_service.py)
 * and the order_action_log CHECK constraint. Keep all three in sync when
 * the lists change — the backend has a test covering its own Python↔SQL
 * pair, but nothing checks this file, so it drifts silently.
 *
 * These are every code the DB accepts. For a dropdown you want
 * selectableReasonCodes(), which drops the system-generated ones.
 */

export type OrderActionType =
  | 'cancel'
  | 'refund'
  | 'reship'
  | 'refire'
  | 'cancel_and_refund'
  | 'cancel_subscription'
  | 'rescript'
  | 'intake_override';

export const ORDER_ACTION_REASON_CODES: Record<OrderActionType, string[]> = {
  cancel: [
    'delayed_patient_requested',
    'duplicate_order',
    'no_Rx_filed',
    'patient_unreachable',
    'fraud_suspected',
    'test_order',
    'refire_internal_cancel',
    'other',
  ],
  refund: [
    'customer_request_unaffordable',
    'duplicate_charge',
    'fraud_claimed',
    'order_never_shipped',
    'processing_delay',
    'lost_by_courier',
    'product_quality_issue',
    'billing_error',
    'goodwill_credit',
    'other',
  ],
  reship: [
    'lost_in_transit',
    'arrived_damaged',
    'short_shipped_supplies',
    'short_shipped_medication',
    'temperature_excursion',
    'wrong_item_shipped',
    'courtesy_replacement',
    'other',
  ],
  refire: [
    'wrong_order_sent',
    'pharmacy_requires_fresh_oid',
    'patient_info_corrected',
    'direction_discrepancy_resolved',
    'rescript_required',
    'other',
  ],
  cancel_and_refund: [
    'duplicate_profiles',
    'duplicate_subscription',
    'dissatisfied_with_service',
    'changing_treatment',
    'switching_plans',
    'signed_up_by_mistake',
    'fraud_suspected',
    'other',
  ],
  cancel_subscription: [
    'duplicate_subscription',
    'dissatisfied_with_service',
    'switching_plans',
    'financial_reasons',
    'treatment_completed',
    'side_effects_or_intolerance',
    'not_seeing_results',
    'dislike_dosage_form',
    'fraud_suspected',
    'other',
  ],
  // Ops-initiated clinician requests (backend app/services/ops_rescript.py)
  rescript: [
    'formulation_change',
    'dose_change',
    'sig_change',
    'pharmacy_change',
    'replace_cancelled_fill',
    'other',
  ],
  intake_override: [
    'intake_verified_on_chart',
    'intake_verified_by_staff',
    'format_switch_same_family',
    'other',
  ],
};

/**
 * Codes the backend writes on its own behalf. Valid in the table, never
 * offered in a dropdown, and rejected if a human submits one (the backend
 * gates on `selectable_reason_codes()`, which excludes these). Mirrors
 * `SYSTEM_ONLY_REASON_CODES` in order_service.py.
 *
 * `refire_internal_cancel`: the refire flow cancels the original OptioRx
 * fill before re-firing it. That's a real cancel row, but a side effect of
 * a refire rather than an independent decision — its own code keeps it out
 * of the `other` bucket so cancel-reason reports stay meaningful.
 */
export const SYSTEM_ONLY_REASON_CODES: ReadonlySet<string> = new Set([
  'refire_internal_cancel',
]);

/** The codes a human may pick for `action` — what the dropdown renders. */
export function selectableReasonCodes(action: OrderActionType): string[] {
  return ORDER_ACTION_REASON_CODES[action].filter(
    (c) => !SYSTEM_ONLY_REASON_CODES.has(c),
  );
}

/** Human-readable labels for the dropdown. Falls back to the raw code
 * (title-cased) for anything not listed here — see reasonCodeLabel().
 * System-only codes get a label too: they never appear in the dropdown,
 * but they DO show up when rendering a past action's reason. */
const REASON_CODE_LABELS: Record<string, string> = {
  // cancel
  delayed_patient_requested: 'Delayed — patient requested',
  duplicate_order: 'Duplicate order',
  no_Rx_filed: 'No Rx filed',
  patient_unreachable: 'Patient unreachable',
  fraud_suspected: 'Fraud suspected',
  test_order: 'Test order',
  refire_internal_cancel: 'Original fill cancelled for refire (automatic)',
  // refund
  customer_request_unaffordable: "Customer request — can't afford",
  duplicate_charge: 'Duplicate charge',
  fraud_claimed: 'Fraud claimed',
  order_never_shipped: 'Order never shipped',
  processing_delay: 'Processing delay',
  lost_by_courier: 'Lost by courier',
  product_quality_issue: 'Product quality issue',
  billing_error: 'Billing error',
  goodwill_credit: 'Goodwill credit',
  // reship
  lost_in_transit: 'Lost in transit',
  arrived_damaged: 'Arrived damaged',
  short_shipped_supplies: 'Short-shipped supplies',
  short_shipped_medication: 'Short-shipped medication',
  temperature_excursion: 'Temperature excursion',
  wrong_item_shipped: 'Wrong item shipped',
  courtesy_replacement: 'Courtesy replacement',
  // refire
  wrong_order_sent: 'Wrong order sent',
  pharmacy_requires_fresh_oid: 'Pharmacy requires fresh OID',
  patient_info_corrected: 'Patient info corrected',
  direction_discrepancy_resolved: 'Direction discrepancy resolved',
  rescript_required: 'Rescript required',
  // cancel_and_refund
  duplicate_profiles: 'Duplicate profiles',
  duplicate_subscription: 'Duplicate subscription',
  dissatisfied_with_service: 'Dissatisfied with service',
  changing_treatment: 'Changing treatment',
  switching_plans: 'Switching plans',
  signed_up_by_mistake: 'Signed up by mistake',
  // cancel_subscription
  financial_reasons: 'Financial reasons',
  treatment_completed: 'Treatment completed',
  side_effects_or_intolerance: 'Side effects or intolerance',
  not_seeing_results: 'Not seeing results',
  dislike_dosage_form: 'Dislike dosage form',
  // rescript
  formulation_change: 'Formulation change',
  dose_change: 'Dose change',
  sig_change: 'Sig / directions change',
  pharmacy_change: 'Pharmacy change',
  replace_cancelled_fill: 'Replace cancelled fill',
  // intake_override
  intake_verified_on_chart: 'Intake verified on chart',
  intake_verified_by_staff: 'Intake verified by staff',
  format_switch_same_family: 'Same-family format switch (intake exempt)',
  // shared
  other: 'Other…',
};

export function reasonCodeLabel(code: string): string {
  return REASON_CODE_LABELS[code] ?? code;
}

/** Mirrors the backend's _ORDER_ACTION_TOOL_MAP (clinical_agent.py admin
 * route) — which clinical-agent tool_name maps to which order-action
 * reason-code list. Tools not present here don't get the dropdown; the
 * plain free-text reason field applies instead. */
export const ORDER_ACTION_TOOL_MAP: Record<string, OrderActionType> = {
  cancel_order: 'cancel',
  refund_charge: 'refund',
  reship_order: 'reship',
  change_order_dose: 'reship',
  refire_order: 'refire',
  rescript_order: 'refire',
  update_order_sig: 'refire',
  cancel_subscription: 'cancel_subscription',
};
